// Soru kartı içindeki köken satırı — Geçmiş Rapor Aktarımı (Task 15, 13A).
//
// 13A tek fiil dili: kart içinde KUTU yok, arka plan yok, yüzde yok.
//   low             → sol AMBER bar + "Kontrol gerekli · s.136" + alıntı (italik)
//   partial         → sol AMBER bar + "2/3 satır aktarıldı · s.136" + "aktarılmayan
//                     satırları gör" (P6; `low` ile birlikteyse low satırının ALTINA gelir)
//   rejected        → sol KIRMIZI bar + "Aktarılmadı · kaynak doğrulanamadı" + "öneriyi gör"
//   accepted-manual → nötr (bar yok) "Öneri elle kabul edildi · s.136" (11A, ✕ yok)
//   badge           → burada hiçbir şey çizilmez (rozet etiket satırında)
// Amber token'lar `--color-warning-text/-border` (ASLA `--color-warning` — o hata
// kırmızısıyla aynı, TODO-DESIGN-01).

import { useState } from "react";
import type { QuestionSnapshot, ResponseProvenance } from "@mahir/shared";
import { fillTemplate } from "../../lib/period-ui";
import {
  IMPORT_COPY,
  scaledSuffixText,
  hasPartialRows,
  provenanceRefLabel,
  rejectedLineText,
  type ProvenanceLineKind,
} from "../../lib/report-import-ui";
import { PartialRowsPopover } from "./PartialRowsPopover";
import { RejectedProposalPopover } from "./RejectedProposalPopover";

export interface ProvenanceLineProps {
  kind: ProvenanceLineKind;
  entry: ResponseProvenance;
  question: QuestionSnapshot;
  /** "bu değeri kullan" → normal `setValue` yolu + oturum içi resolved damgası. */
  onUseProposal: () => void;
  /**
   * F7 — kart etiket satırında köken rozeti (`s.136`) çizildi mi? Çizildiyse kısmi
   * kabul satırı ref'i TEKRAR ETMEZ (aynı kartta iki kez görünüyordu).
   */
  badgeShown?: boolean;
}

const lineTextStyle = { fontSize: "0.78rem", lineHeight: 1.5 } as const;

/**
 * P8(c) — kırpılan değerde satır sonuna " · kısaltıldı" eklenir.
 * P15/1b — ölçeklenen değerde " · ×{çarpan} ölçeklendi" (rozetle AYNI sözlük); çarpan
 * köken satırındaki `scaleFactor`'dan gelir, metin sabit DEĞİLDİR.
 */
function trimmedSuffix(entry: ResponseProvenance): string {
  if (entry.method === "ai-import-trim") return ` · ${IMPORT_COPY.trimmedSuffix}`;
  if (entry.method === "ai-import-scaled") {
    const text = scaledSuffixText(entry.scaleFactor);
    return text === null ? "" : ` · ${text}`;
  }
  return "";
}

export function ProvenanceLine(props: ProvenanceLineProps): JSX.Element | null {
  const { kind, entry, question, onUseProposal, badgeShown = false } = props;
  const [proposalOpen, setProposalOpen] = useState(false);
  const [partialOpen, setPartialOpen] = useState(false);
  const ref = provenanceRefLabel(entry);
  const refText = ref?.label ?? "—";

  // P6 — kısmi kabul satırı; `low` ile birlikteyse onun ALTINDA ayrı satır olarak durur.
  const partial = entry.partial;
  const partialBlock =
    hasPartialRows(entry) && partial ? (
      <>
        <div
          className="mt-1"
          style={{
            borderLeft: "3px solid var(--color-warning-border)",
            paddingLeft: 8,
            color: "var(--color-warning-text)",
            ...lineTextStyle,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {fillTemplate(badgeShown ? IMPORT_COPY.partialLineNoRef : IMPORT_COPY.partialLine, {
              accepted: partial.acceptedRows,
              total: partial.totalRows,
              ref: refText,
            })}
          </span>{" "}
          <button
            type="button"
            onClick={() => setPartialOpen(true)}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              color: "var(--color-warning-text)",
              textDecoration: "underline",
              cursor: "pointer",
              font: "inherit",
            }}
          >
            {IMPORT_COPY.seePartialRows}
          </button>
        </div>
        <PartialRowsPopover
          open={partialOpen}
          partial={partial}
          question={question}
          onClose={() => setPartialOpen(false)}
        />
      </>
    ) : null;

  if (kind === "badge") return null;

  if (kind === "accepted-manual") {
    return (
      <p className="mt-1" style={{ ...lineTextStyle, color: "var(--color-text-2)" }}>
        {fillTemplate(IMPORT_COPY.proposalAccepted, { ref: refText })}
      </p>
    );
  }

  if (kind === "partial") return partialBlock;

  if (kind === "low") {
    return (
      <>
        <div
          className="mt-1"
          style={{
            borderLeft: "3px solid var(--color-warning-border)",
            paddingLeft: 8,
            color: "var(--color-warning-text)",
            ...lineTextStyle,
          }}
        >
          <span style={{ fontWeight: 600 }}>
            {/* run2-2 (a) — ayrıntı varsa sebebi satırda; yoksa metin birebir eski. */}
            {entry.detail
              ? fillTemplate(IMPORT_COPY.lowConfidenceLineWithDetail, {
                  ref: refText,
                  detail: entry.detail,
                })
              : fillTemplate(IMPORT_COPY.lowConfidenceLine, { ref: refText })}
            {trimmedSuffix(entry)}
          </span>
          {entry.snippet ? <span style={{ fontStyle: "italic" }}> — {entry.snippet}</span> : null}
        </div>
        {partialBlock}
      </>
    );
  }

  // kind === "rejected"
  return (
    <>
      <div
        className="mt-1"
        style={{
          borderLeft: "3px solid var(--color-error)",
          paddingLeft: 8,
          color: "var(--color-text-2)",
          ...lineTextStyle,
        }}
      >
        {/* İNCELEME (Important) — köken ayrıntısı varsa sebebi satırda göster. */}
        <span>{rejectedLineText(entry.detail)}</span>{" "}
        <button
          type="button"
          onClick={() => setProposalOpen(true)}
          style={{
            background: "none",
            border: "none",
            padding: 0,
            color: "var(--color-text-2)",
            textDecoration: "underline",
            cursor: "pointer",
            font: "inherit",
          }}
        >
          {IMPORT_COPY.seeProposal}
        </button>
      </div>
      <RejectedProposalPopover
        open={proposalOpen}
        entry={entry}
        question={question}
        onUse={() => {
          setProposalOpen(false);
          onUseProposal();
        }}
        onClose={() => setProposalOpen(false)}
      />
    </>
  );
}
